import type { CustomCommunity4 } from '~/types'

/**
 * Structured data of the "L'avis des joueurs" page: the reviewed item at the root, its rating nested under it.
 * An AggregateRating at the root with an itemReviewed pointing back to it is refused by Google
 * ("a nested <parent_node> object cannot contain itemReviewed").
 */
export const useAggregateRatingSchema = ({
  community,
}: {
  community: Ref<CustomCommunity4 | undefined>
}) => {
  const route = useRoute()
  const runtimeConfig = useRuntimeConfig()
  const { baseUrl } = runtimeConfig.public

  return computed(() => {
    const rawCommunity = unref(community)
    const typeSlug = rawCommunity?.type?.slug

    if (
      !typeSlug ||
      typeSlug === 'libre' ||
      !rawCommunity?.stats?.total_review ||
      !rawCommunity.stats.rating
    ) {
      return {}
    }

    const commonSchema = {
      '@context': 'https://schema.org',
      name: rawCommunity.title,
      url: `${baseUrl}/communautes/${rawCommunity.slug}`,
      aggregateRating: {
        '@type': 'AggregateRating',
        url: `${baseUrl}${route.path}`,
        reviewCount: rawCommunity.stats.total_review,
        ratingValue: rawCommunity.stats.rating,
        // fixed value
        worstRating: '1',
        bestRating: '5',
      },
    }

    switch (typeSlug) {
      case 'service':
        return jsonLdScript(
          {
            '@type': 'SoftwareApplication',
            ...commonSchema,
            applicationCategory: 'game',
          },
          'aggregate-rating-schema',
        )

      case 'console':
        return jsonLdScript(
          {
            '@type': 'Product',
            ...commonSchema,
            brand: {
              '@type': 'Brand',
              name: 'Sony',
            },
            description: rawCommunity.description,
            image:
              rawCommunity.image_og?.url || rawCommunity.image_desktop?.url,
          },
          'aggregate-rating-schema',
        )

      case 'jeu':
      default: {
        const platforms =
          rawCommunity.consoles?.map((item) => item.name) || undefined

        return jsonLdScript(
          {
            '@type': 'VideoGame',
            ...commonSchema,
            applicationCategory: 'game',
            gamePlatform: platforms,
            operatingSystem: platforms,
          },
          'aggregate-rating-schema',
        )
      }
    }
  })
}
